import { Grid, TextField, Autocomplete } from '@mui/material';
import {
    parse,
    format,
    addMinutes,
    isBefore,
    isEqual,
    isValid,
} from 'date-fns';

type TimeOption = {
    minutes: number;
    label: string;
    hhmmss: string;
};

type TimeRangeOptions = {
    includeMaxTime?: boolean;
};

/**
 * Normalizes a time string to HH:mm format.
 * Handles HH:mm, HH:mm:ss, and other common formats.
 * Special case: "24:00" is converted to "23:59" for parsing purposes.
 */
function normalizeTimeString(time: string): string {
    if (!time) return '00:00';
    // Extract only HH:mm part (first 5 characters if format is HH:mm or HH:mm:ss)
    const match = time.match(/^(\d{1,2}):(\d{2})/);
    if (match && match[1] && match[2]) {
        const hours = match[1].padStart(2, '0');
        const minutes = match[2];
        // Handle 24:00 as end of day (date-fns doesn't accept hour 24)
        if (hours === '24') {
            return '23:59';
        }
        return `${hours}:${minutes}`;
    }
    return '00:00';
}

/**
 * Safely parses a time string into a Date object.
 * Returns a valid Date or null if parsing fails.
 */
function safeParseTime(timeStr: string, referenceDate: Date): Date | null {
    const normalized = normalizeTimeString(timeStr);
    const parsed = parse(normalized, 'HH:mm', referenceDate);
    return isValid(parsed) ? parsed : null;
}

type FormChangeEvent = {
    name: string | undefined;
    value: string;
};

type SttFormTimeProps = {
    grid?: boolean;
    name?: string;
    caption?: string;
    defVal?: string;
    min?: string;
    max?: string;
    interval?: number;
    disableClearable?: boolean;
    disabled?: boolean;
    onChange?: (event: FormChangeEvent) => void;
    timeRangeOptions?: TimeRangeOptions;
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    [key: string]: any;
};

/**
 * Generates an array of time options based on the given min time, max time, and interval.
 */
function getTimeRangeOptions(
    minTime: string,
    maxTime: string,
    interval: number,
    options: TimeRangeOptions = {},
): TimeOption[] {
    let times: TimeOption[] = [];
    // Use a reference date for parsing time strings
    const referenceDate = new Date(2000, 0, 1);
    const parsedMin = safeParseTime(minTime, referenceDate);
    const parsedMax = safeParseTime(maxTime, referenceDate);

    // Return empty array if parsing fails
    if (!parsedMin || !parsedMax) {
        return times;
    }

    let currentTime = parsedMin;
    const max = parsedMax;

    while (isBefore(currentTime, max) || isEqual(currentTime, max)) {
        const hours = currentTime.getHours();
        const minutes = currentTime.getMinutes();
        times.push({
            minutes: hours * 60 + minutes,
            label: format(currentTime, 'HH:mm'),
            hhmmss: format(currentTime, 'HH:mm:ss'),
        });
        currentTime = addMinutes(currentTime, interval);
    }
    if (options.includeMaxTime) {
        const maxHours = max.getHours();
        const maxMinutes = max.getMinutes();
        times.push({
            minutes: maxHours * 60 + maxMinutes,
            label: format(max, 'HH:mm'),
            hhmmss: format(max, 'HH:mm:ss'),
        });
    } else {
        times = times.filter((e: any) => e.minutes % interval === 0);
    }
    return times;
}

/**
 * Form time component.
 * Presents a dropdown list of time options with a specified interval, starting at a given minimum time and ending at a given maximum time.
 */
export function SttFormTime({
    grid = false,
    name,
    caption,
    defVal,
    min = '06:00:00',
    max = '23:55:00',
    interval = 30,
    disableClearable = true,
    disabled = false,
    onChange,
    timeRangeOptions = { includeMaxTime: false },
    xs,
    sm,
    md,
    lg,
    xl,
    ...rest
}: SttFormTimeProps): React.ReactElement {
    const times = getTimeRangeOptions(min, max, interval, timeRangeOptions);

    // Main content
    const content = (
        <Autocomplete
            disabled={disabled}
            options={times}
            getOptionLabel={(option: any) =>
                typeof option === 'string' ? option : option?.label || ''
            }
            value={
                times.find(
                    (time: any) =>
                        time?.label === defVal || time?.hhmmss === defVal,
                ) || null
            }
            onChange={(_, time) =>
                onChange?.({
                    name: name || '',
                    value: time?.label || '',
                })
            }
            disableClearable={disableClearable}
            isOptionEqualToValue={(option, value) =>
                option?.label === value?.label
            }
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    fullWidth
                    size="small"
                    label={caption}
                    variant="outlined"
                />
            )}
        />
    );

    // Wrapped by grid
    const sizeProps = { xs, sm, md, lg, xl };
    return grid ? (
        <Grid size={sizeProps} {...rest}>
            {content}
        </Grid>
    ) : (
        content
    );
}
